import { browserTimezone } from '../components/collections/ScheduleFields';
import { isKnownZone, wallPartsIn } from './zonedClock';

/**
 * What heads Home (KPR-1122): a time-of-day greeting by default, or the title
 * the person wrote (the `homeTitle` preference). Pure, so the page and its
 * tests share one answer.
 *
 *   05:00-11:59  Good morning
 *   12:00-16:59  Good afternoon
 *   17:00-04:59  Good evening
 *
 * by the person's own zone preference, else the browser's.
 */

export const HOME_TITLE_MAX = 60;

// Mirrors keepr-api utils/preferenceUtils.js (homeTitle): control characters,
// the line and paragraph separators, and `<` / `>` (markup) are refused.
// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[\u0000-\u001f\u007f-\u009f\u2028\u2029<>]/;

/**
 * The text a person typed → { value } (trimmed; null when it is empty, which
 * returns Home to its greeting) or { error } saying what to change.
 */
export function readHomeTitle(text) {
	const value = typeof text === 'string' ? text.trim() : '';
	if (!value) return { value: null };
	if (value.length > HOME_TITLE_MAX) return { error: `Use ${HOME_TITLE_MAX} characters or fewer.` };
	if (FORBIDDEN.test(value)) return { error: 'Use plain text on one line, without < or >.' };
	return { value };
}

/** The zone Home reads the clock in: the preference when the runtime knows it, else the browser's. */
export function homeZone(preferred) {
	if (typeof preferred === 'string' && preferred && isKnownZone(preferred)) return preferred;
	const device = browserTimezone();
	return isKnownZone(device) ? device : 'UTC';
}

export function greetingPeriod(now, zone) {
	const { hh } = wallPartsIn(zone, now);
	if (hh >= 5 && hh < 12) return 'morning';
	if (hh >= 12 && hh < 17) return 'afternoon';
	return 'evening';
}

/** "Good morning, Ada"; without a first name, "Good morning". */
export function homeGreeting({ now = new Date(), zone, firstName } = {}) {
	const name = typeof firstName === 'string' ? firstName.trim() : '';
	const base = `Good ${greetingPeriod(now, zone)}`;
	return name ? `${base}, ${name}` : base;
}

const HOUR_MS = 3600 * 1000;

/**
 * Milliseconds until the greeting could next change (the next 05:00, 12:00 or
 * 17:00 on the zone's clock), capped at an hour so a clock change or a
 * sleeping laptop is caught up on the next tick.
 */
export function msUntilGreetingChanges(now, zone) {
	const { hh, mm, ss } = wallPartsIn(zone, now);
	const into = ((hh * 60 + mm) * 60 + ss) * 1000;
	const day = 24 * HOUR_MS;
	const next = [5, 12, 17].map(h => h * HOUR_MS).find(boundary => boundary > into) ?? (day + 5 * HOUR_MS);
	return Math.min(next - into, HOUR_MS);
}
